import { useEffect, useState } from "react";

import { compareVersions } from "@bigbear/shared";

import { downloadUrlsFor, fetchLatestRelease, type LatestRelease } from "./api/updates";
import { latestRelease } from "./releases";

export interface LatestDownload {
  readonly version: string;
  readonly date: string;
  readonly headline: string;
  readonly nsisUrl: string;
  readonly msiUrl: string;
  /** Only known once the live manifest has responded — the build-time
   * fallback has no checksums to offer. */
  readonly nsisSha256?: string;
  readonly msiSha256?: string;
  /**
   * `"building"` covers the gap between a VERSION-bump commit reaching the
   * portal (an instant rebuild) and its installers reaching R2 (a slower,
   * separate Windows build in the same CI run) — see the release job in
   * `.github/workflows/desktop.yml`. Rare, and only ever for a few minutes,
   * but a dead link in that window would be worse than a short-lived label.
   */
  readonly status: "ready" | "building";
}

const fallback: LatestDownload | null = latestRelease
  ? {
      version: latestRelease.version,
      date: latestRelease.date,
      headline: latestRelease.headline,
      ...downloadUrlsFor(latestRelease.version),
      status: "ready",
    }
  : null;

/** `exactOptionalPropertyTypes` rejects assigning `undefined` to an optional
 * field directly, so an absent checksum is represented by omitting the key
 * rather than by setting it to `undefined`. */
function checksums(manifest: LatestRelease): Pick<LatestDownload, "nsisSha256" | "msiSha256"> {
  return manifest.nsisSha256 && manifest.msiSha256
    ? { nsisSha256: manifest.nsisSha256, msiSha256: manifest.msiSha256 }
    : {};
}

/**
 * The newest download, shown instantly from the committed release notes
 * (`releases/*.md`, baked in at build time) and upgraded in place once the
 * live manifest — the same one the desktop app's updater polls — responds.
 * Never blocks on the network: there is always something to render.
 */
export function useLatestDownload(): LatestDownload | null {
  const [download, setDownload] = useState<LatestDownload | null>(fallback);

  useEffect(() => {
    const controller = new AbortController();

    fetchLatestRelease(controller.signal)
      .then((manifest) => {
        if (controller.signal.aborted) return;

        if (!fallback || manifest.version === fallback.version) {
          setDownload({
            version: manifest.version,
            date: fallback?.date ?? manifest.pubDate.slice(0, 10),
            headline: fallback?.headline ?? manifest.notes,
            nsisUrl: manifest.nsisUrl,
            msiUrl: manifest.msiUrl,
            ...checksums(manifest),
            status: "ready",
          });
        } else if (compareVersions(manifest.version, fallback.version) > 0) {
          // R2 has a version the portal's build doesn't know about yet — a
          // rebuild is presumably already on its way, but show it now rather
          // than make a visitor wait.
          setDownload({
            version: manifest.version,
            date: manifest.pubDate.slice(0, 10),
            headline: manifest.notes,
            nsisUrl: manifest.nsisUrl,
            msiUrl: manifest.msiUrl,
            ...checksums(manifest),
            status: "ready",
          });
        } else if (fallback) {
          // The portal already knows about a version newer than what R2 is
          // currently serving — its release-notes commit landed, but the
          // installer build is still in flight.
          setDownload({ ...fallback, status: "building" });
        }
      })
      .catch(() => {
        // Network hiccup or the manifest briefly unreachable — the build-time
        // fallback is already showing, so there's nothing more to do.
      });

    return () => controller.abort();
  }, []);

  return download;
}
